import sum from "lodash/sum";
import updateArrayMember from "@Array/updateArrayMember";
import RouteType from "@Route/types/Route.type";
import PassengerGroupWithPricesType from "@SearchResults/types/passenger/PassengerGroupWithPrices.type";
import PassengerGroupWithPricesAndCountType from "@SearchResults/types/passenger/PassengerGroupWithPricesAndCount.type";

/**
 * Returns active passenger groups
 * @param route {object}
 * @param passengers {object}
 * @param passengerGroups {array}
 * @returns {*|*[]}
 */

interface FilterActivePassengerGroupsAttributes {
    route: RouteType,
    passengers: any,
    passengerGroups: PassengerGroupWithPricesType[],
}

export default function filterActivePassengerGroups(props: FilterActivePassengerGroupsAttributes) {
    const {
        route,
        passengers,
        passengerGroups,
    } = props

    const routeActive = route.status.status === 'open'
    const {
        passengersAdult,
        passengersPensioner,
        passengersChildren,
        passengersStudent,
        passengers: totalPassengers,
    } = passengers

    const getNumberOfPassengersForGroup = (group): number => {
        const {
            isNormal,
            isPensioner,
            isChild,
            isStudent,
        } = group

        const groupTypes = {
            [isNormal]: passengersAdult,
            [isPensioner]: passengersPensioner,
            [isChild]: passengersChildren,
            [isStudent]: passengersStudent,
            [isStudent && isChild]: passengersStudent + passengersChildren,
            [isStudent && isPensioner]: passengersStudent + passengersPensioner,
            [isStudent && isPensioner && isChild]: passengersStudent + passengersPensioner + passengersChildren,
        }

        // @ts-ignore
        return groupTypes.true
    }

    const addPassengerCountToGroups = (passengerGroups: PassengerGroupWithPricesType[]) => (
        passengerGroups.map((group): PassengerGroupWithPricesAndCountType => ({
            ...group,
            numberOfPassengers: getNumberOfPassengersForGroup(group),
        }))
    )
    /**
     * Gets the cheapest option to show to user (e.g. if we have 2 child classes for this route, show the cheaper if user selected a child passenger)
     */
    const getCheapestOptions = () => {
        const sortByPrice = (passengerGroup1: PassengerGroupWithPricesType, passengerGroup2: PassengerGroupWithPricesType) => (
            passengerGroup1.priceWithDiscount > passengerGroup2.priceWithDiscount ? 1 : 0
        )

        const groupedRoutePassengerGroups = passengerGroups.reduce((acc, passengerGroup) => {
            if (passengerGroup.isChild) {
                acc.childClasses = [...acc.childClasses, passengerGroup]
            }

            if (passengerGroup.isStudent) {
                acc.studentClasses = [...acc.studentClasses, passengerGroup]
            }

            if (passengerGroup.isPensioner) {
                acc.pensionerClasses = [...acc.pensionerClasses, passengerGroup]
            }
            return acc
        }, {childClasses: [], studentClasses: [], pensionerClasses: []})

        groupedRoutePassengerGroups.childClasses.sort(sortByPrice)
        groupedRoutePassengerGroups.studentClasses.sort(sortByPrice)
        groupedRoutePassengerGroups.pensionerClasses.sort(sortByPrice)

        return [
            ...passengerGroups.filter((passengerGroup) => (!passengerGroup.isStudent && !passengerGroup.isChild && !passengerGroup.isPensioner)),
            ...(groupedRoutePassengerGroups.childClasses.length ? [groupedRoutePassengerGroups.childClasses[0]] : []),
            ...(groupedRoutePassengerGroups.studentClasses.length ? [groupedRoutePassengerGroups.studentClasses[0]] : []),
            ...(groupedRoutePassengerGroups.pensionerClasses.length ? [groupedRoutePassengerGroups.pensionerClasses[0]] : []),
        ]
    }

    const addMissingPassengerGroupsAsNormal = () => {
        let passengerGroups = addPassengerCountToGroups(getCheapestOptions())
        const totalPassengersInGroups = sum(passengerGroups.map((group) => (group.numberOfPassengers)))

        if (totalPassengersInGroups < totalPassengers) {
            passengerGroups = updateArrayMember(
                passengerGroups,
                0,
                'numberOfPassengers',
                totalPassengers - totalPassengersInGroups + passengerGroups[0].numberOfPassengers
            )
        }

        return passengerGroups
    }

    const filterOnlyGroupsWithPassengers = () => (
        addMissingPassengerGroupsAsNormal().filter((group) => group.numberOfPassengers > 0 ?? group)
    )

    return routeActive ? filterOnlyGroupsWithPassengers() : []
}